import { ZodSchema } from "zod";

export class APIError extends Error {
  response: Response;
  data: unknown;

  constructor(response: Response, data: unknown, message?: string) {
    super(message || `HTTP Error: ${response.status}`);
    this.name = "APIError";
    this.response = response;
    this.data = data;
  }
}

export async function safeFetch<T>(
  schema: ZodSchema<T>,
  ...args: Parameters<typeof fetch>
): Promise<T> {
  const response = await fetch(...args);

  if (!response.ok) {
    let errorData: unknown;
    if (response.headers.get("Content-Type") === "application/json") {
      try {
        errorData = await response.json();
      } catch {
        errorData = "Failed to parse JSON error response";
      }
    } else {
      try {
        errorData = await response.text();
      } catch {
        errorData = "Failed to parse error response";
      }
    }

    throw new APIError(response, errorData);
  }

  if (
    response.status === 204 ||
    response.status === 205 ||
    response.headers.get("Content-Length") === "0"
  ) {
    const parseResult = schema.safeParse(undefined);
    if (parseResult.success) {
      return parseResult.data;
    }

    throw new APIError(
      response,
      null,
      `Expected response body, but got empty response with status ${response.status}`,
    );
  }

  let responseData: unknown;
  try {
    responseData = await response.json();
  } catch (error) {
    throw new APIError(response, error, "Failed to parse JSON response");
  }

  try {
    return schema.parse(responseData);
  } catch (error) {
    throw new APIError(response, error, "Response validation failed");
  }
}
